import React, { Dispatch, SetStateAction, forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { Box, Stack } from '@mantine/core';
import { Content, EditorContent } from '@tiptap/react';
import { useEditorStyles } from 'pages/SmartEditor/editorStyles';
import { useEditorConfig } from 'pages/SmartEditor/hooks/useEditorConfig';
import { useRegisterEditorEvents } from 'pages/SmartEditor/hooks/useEditorEvents';
import { SmartEditorBubbleMenu } from 'pages/SmartEditor/SmartEditorBubbleMenu';
import { SmartEditorFloatingMenu } from 'pages/SmartEditor/SmartEditorFloatingMenu';

interface SmartEditorProps {
  fileContent: Content;
  onChange?: () => void;
  setPendingChanges?: Dispatch<SetStateAction<boolean>>;
}

// Editor ref exposed to parent components
export type EditorRef = { exportEditorContent: () => Content; clearEditorContent: () => void } | null;

export const SmartEditor = forwardRef(({ fileContent, setPendingChanges, onChange }: SmartEditorProps, ref) => {
  const editorContentRef = useRef<HTMLDivElement | null>(null);
  const [isLinkInputActive, setIsLinkInputActive] = useState(false);

  const onUpdate = () => {
    if (setPendingChanges) setPendingChanges(true);
    if (onChange) onChange();
  };

  const editor = useEditorConfig({ fileContent, onUpdate });
  useRegisterEditorEvents({ editor, editorContentRef });

  const { classes } = useEditorStyles();

  // Expose functions to parent components
  useImperativeHandle(ref, () => ({
    exportEditorContent: () => editor?.getJSON(),
    clearEditorContent: () => editor?.commands.clearContent()
  }));

  // Only render editor when it's ready
  if (!editor) return null;

  return (
    <Box ref={editorContentRef} w="80%" sx={{ backgroundColor: 'white' }}>
      <Stack>
        <Box>
          <SmartEditorBubbleMenu editor={editor} setIsLinkInputActive={setIsLinkInputActive} />
          <SmartEditorFloatingMenu editor={editor} isLinkInputActive={isLinkInputActive} />
          <EditorContent editor={editor} className={classes.content} />
        </Box>
      </Stack>
    </Box>
  );
});

SmartEditor.displayName = 'SmartEditor';
